import {
  ComputeBudgetProgram,
  LAMPORTS_PER_SOL,
  PublicKey,
  Transaction,
  TransactionMessage,
  VersionedTransaction,
} from "@solana/web3.js";
import { BN, Program } from "@coral-xyz/anchor";
import { PumpFunIDL } from "@/pumpfun-idl";
import { BondingCurveAccount } from "@/utils/pumpfun/bondingCurveAccount";
import { GlobalAccount } from "@/utils/pumpfun/globalAccount";
import {
  createAssociatedTokenAccountInstruction,
  getAccount,
  getAssociatedTokenAddress,
} from "@solana/spl-token";
const GLOBAL_ACCOUNT_SEED = "global";
const BONDING_CURVE_SEED = "bonding-curve";
const PUMP_FUN_PROGRAM = new PublicKey(
  "6EF8rrecthR5Dkzon8Nwu78hRvfCKubJ14M5uBEwF6P"
);
const pumpfun = {
  namespaced: true,
  state: {
    program: null,
    bondingCurve: null,
    associatedBondingCurve: null,
    bondingCurveComplete: false,
    commitment: "confirmed",
    finality: "finalized",
  },
  mutations: {
    setProgram(state, payload) {
      state.program = payload;
    },
    setBondingCurve(state, payload) {
      state.bondingCurve = payload;
    },
    setAssociatedBondingCurve(state, payload) {
      state.associatedBondingCurve = payload;
    },
    setBondingCurveComplete(state, payload) {
      state.bondingCurveComplete = payload;
    },
    setCommitment(state, payload) {
      state.commitment = payload;
    },
    setFinality(state, payload) {
      state.finality = payload;
    },
  },
  getters: {
    getBondingCurve: (state, getters, rootState) => {
      return PublicKey.findProgramAddressSync(
        [Buffer.from(BONDING_CURVE_SEED), rootState.solana.mint.toBuffer()],
        PUMP_FUN_PROGRAM
      )[0];
    },
    getAssociatedBondingCurve: async (state, getters, rootState) => {
      let bondingCurve = state.bondingCurve;
      if (!bondingCurve) {
        bondingCurve = getters["getBondingCurve"];
      }
      if (bondingCurve) {
        return await getAssociatedTokenAddress(
          rootState.solana.mint,
          bondingCurve,
          true
        );
      }
      return null;
    },
    async getBondingCurveAccount(state, getters, rootState) {
      let bondingCurve = state.bondingCurve;
      if (!bondingCurve) {
        bondingCurve = getters["getBondingCurve"];
      }
      if (bondingCurve) {
        const tokenAccount = await rootState.solana.connection.getAccountInfo(
          bondingCurve,
          state.commitment
        );
        if (!tokenAccount) {
          return null;
        }
        return BondingCurveAccount.fromBuffer(tokenAccount.data);
      }
      return null;
    },
    async getGlobalAccount(state, getters, rootState) {
      const [globalAccount] = PublicKey.findProgramAddressSync(
        [Buffer.from(GLOBAL_ACCOUNT_SEED)],
        PUMP_FUN_PROGRAM
      );
      const tokenAccount = await rootState.solana.connection.getAccountInfo(
        globalAccount,
        state.commitment
      );
      return GlobalAccount.fromBuffer(tokenAccount.data);
    },
  },
  actions: {
    async init({ rootState, getters, commit }) {
      const program = new Program(PumpFunIDL, rootState.solana.connection);
      commit("setProgram", program);
      commit("setBondingCurve", getters["getBondingCurve"]);
      commit(
        "setAssociatedBondingCurve",
        await getters["getAssociatedBondingCurve"]
      );
      const bondingCurveAccount = await getters["getBondingCurveAccount"];
      commit(
        "setBondingCurveComplete",
        bondingCurveAccount && bondingCurveAccount.complete
      );
    },
    async sendTransaction({ state, rootState, rootGetters }, payload) {
      const { tx, payer, signers } = payload;
      const newTx = new Transaction();
      const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
        units: 3_000_000,
      });
      const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: rootState.solana.priorityFee * LAMPORTS_PER_SOL,
      });
      newTx.add(modifyComputeUnits);
      newTx.add(addPriorityFee);
      newTx.add(tx);

      const blockHash = (
        await rootState.solana.connection.getLatestBlockhash(state.commitment)
      ).blockhash;
      const messageV0 = new TransactionMessage({
        payerKey: payer,
        recentBlockhash: blockHash,
        instructions: newTx.instructions,
      }).compileToV0Message();

      const versionedTx = new VersionedTransaction(messageV0);
      versionedTx.sign(signers);
      const signature = await rootState.solana.connection.sendTransaction(
        versionedTx,
        { skipPreflight: false }
      );
      const txResult = await rootGetters["solana/getTxDetails"](signature);
      if (!txResult) {
        throw new Error("Transaction failed!");
      }
      return signature;
    },
    async buy({ state, rootState, getters, rootGetters, dispatch }, payload) {
      let { wallet, amount } = payload;
      // eslint-disable-next-line no-undef
      amount = BigInt((amount * LAMPORTS_PER_SOL).toFixed(0));
      const sender = rootGetters["solana/getSender"](wallet.privateKey);
      const bondingCurveAccount = await getters["getBondingCurveAccount"];
      if (!bondingCurveAccount) {
        throw new Error("Bonding curve account not found!");
      }
      if (bondingCurveAccount.complete) {
        throw new Error("Bonding Curve Completed!");
      }
      amount = bondingCurveAccount.getBuyPrice(amount);
      const maxSolCost =
        // eslint-disable-next-line no-undef
        amount + (amount * BigInt(rootState.solana.slippage * 100)) / 10000n;
      const globalAccount = await getters["getGlobalAccount"];
      const associatedUser = await getAssociatedTokenAddress(
        rootState.solana.mint,
        sender.publicKey,
        false
      );
      const tx = new Transaction();
      try {
        await getAccount(
          rootState.solana.connection,
          associatedUser,
          state.commitment
        );
      } catch (e) {
        tx.add(
          createAssociatedTokenAccountInstruction(
            sender.publicKey,
            associatedUser,
            sender.publicKey,
            rootState.solana.mint
          )
        );
      }
      tx.add(
        await state.program.methods
          .buy(new BN(amount.toString()), new BN(maxSolCost.toString()))
          .accounts({
            feeRecipient: globalAccount.feeRecipient,
            mint: rootState.solana.mint,
            associatedBondingCurve: state.associatedBondingCurve,
            associatedUser: associatedUser,
            user: sender.publicKey,
          })
          .transaction()
      );
      return await dispatch("sendTransaction", {
        tx,
        payer: sender.publicKey,
        signers: [sender],
      });
    },
    async sell({ state, rootState, getters, rootGetters, dispatch }, payload) {
      let { wallet, amount } = payload;
      // eslint-disable-next-line no-undef
      amount = BigInt(
        (amount * Math.pow(10, rootState.solana.metadata.decimals)).toFixed(0)
      );
      const sender = rootGetters["solana/getSender"](wallet.privateKey);
      const bondingCurveAccount = await getters["getBondingCurveAccount"];
      if (!bondingCurveAccount) {
        throw new Error("Bonding curve account not found!");
      }
      if (bondingCurveAccount.complete) {
        throw new Error("Bonding Curve Completed!");
      }
      const globalAccount = await getters["getGlobalAccount"];
      let minSolOutput = bondingCurveAccount.getSellPrice(
        amount,
        globalAccount.feeBasisPoints
      );
      minSolOutput =
        minSolOutput -
        // eslint-disable-next-line no-undef
        (minSolOutput * BigInt(rootState.solana.slippage * 100)) / 10000n;
      const associatedUser = await getAssociatedTokenAddress(
        rootState.solana.mint,
        sender.publicKey,
        false
      );

      const tx = new Transaction();
      tx.add(
        await state.program.methods
          .sell(new BN(amount.toString()), new BN(minSolOutput.toString()))
          .accounts({
            feeRecipient: globalAccount.feeRecipient,
            mint: rootState.solana.mint,
            associatedBondingCurve: state.associatedBondingCurve,
            associatedUser: associatedUser,
            user: sender.publicKey,
          })
          .transaction()
      );
      return await dispatch("sendTransaction", {
        tx,
        payer: sender.publicKey,
        signers: [sender],
      });
    },
  },
};

export default pumpfun;
